import React, { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "../axios";
import RegularBus from "../components/RegularBus";
import "../styles/analysis.css";

const DAY_TYPES = [
  { key: "weekday", label: "平日" },
  { key: "sat", label: "土曜" },
  { key: "holiday", label: "日祝" },
];

export default function AnalysisRoutePage() {
  const { routeId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const dayType = searchParams.get("day_type") || "weekday";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/analysis/routes/${routeId}/trips?day_type=${dayType}`);
      setData(res.data);
    } catch (e) {
      setError(e.response?.status === 404 ? "その路線は見つかりません。" : "取得に失敗しました");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [routeId, dayType]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="anl-page">
      <button className="anl-back" onClick={() => navigate("/analysis")}>運用解析</button>

      <div className="anl-header">
        <h2 className="anl-title">
          {loading || !data ? "路線別の運用" : data.route.short_name}
          {data && <span className="anl-badge anl-badge--depot">{data.route.depot}</span>}
        </h2>
        {data && <p className="anl-subtitle">{data.route.full_name}</p>}
      </div>

      <div className="anl-tabs">
        {DAY_TYPES.map((t) => (
          <button
            key={t.key}
            className={`anl-tab ${dayType === t.key ? "anl-tab--active" : ""}`}
            onClick={() => setSearchParams({ day_type: t.key })}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="anl-empty">読み込み中…</p>
      ) : error ? (
        <div className="anl-warning">{error}</div>
      ) : data.trips.length === 0 ? (
        <div className="anl-card">
          <p className="anl-empty">当該ダイヤ種別の便は登録されていません。</p>
        </div>
      ) : (
        <div className="anl-card">
          <div className="anl-card__header">
            便の一覧
            <span style={{ fontWeight: 400, color: "#6b7280" }}>{data.trips.length}便</span>
          </div>
          <div className="anl-table-wrap">
            <table className="anl-table">
              <thead>
                <tr>
                  <th>発</th>
                  <th>始発地</th>
                  <th>乗番</th>
                  <th>本日充当</th>
                  <th>所定</th>
                  <th>運用の多い車両（直近30日）</th>
                </tr>
              </thead>
              <tbody>
                {data.trips.map((t) => (
                  <tr key={t.trip_id}>
                    <td className="anl-table__num" style={{ fontWeight: 600 }}>{t.departure_time}</td>
                    <td>
                      {t.start}
                      {t.route_no && <span className="anl-no">{t.route_no}</span>}
                    </td>
                    <td>
                      {(t.duties ?? []).length === 0 ? (
                        <span style={{ color: "#9ca3af" }}>—</span>
                      ) : (
                        t.duties.map((d) => (
                          <span key={d.id} style={{ marginRight: ".5rem", display: "inline-block" }}>
                            <button
                              className="anl-link"
                              onClick={() => navigate(`/analysis/duties/${d.id}`)}
                              title={`${d.code}の${d.trip_count}便中${d.seq}便目`}
                            >
                              {d.code}
                            </button>
                            {/* 1日の流れのどのあたりの便かを添える。
                                乗番名だけだと朝の便か夕方の便か分からない。 */}
                            <span style={{ color: "#9ca3af", fontSize: ".75rem" }}>
                              ({d.seq}/{d.trip_count})
                            </span>
                          </span>
                        ))
                      )}
                    </td>
                    <td>
                      {/* 確定（実際に目撃）と推定（乗番からの当て推量）を
                          混ぜない。推定は薄く出して、事実と区別できるようにする。 */}
                      {t.today ? (
                        <button
                          className={`anl-link${t.today.confirmed ? "" : " anl-link--tentative"}`}
                          onClick={() =>
                            navigate(`/analysis/buses/${encodeURIComponent(t.today.bus_number)}?depot=${encodeURIComponent(t.today.depot)}`)
                          }
                          title={
                            t.today.confirmed
                              ? `${t.today.bus_number}（目撃で確定）`
                              : `${t.today.bus_number}（乗番からの推定。まだ目撃されていません）`
                          }
                        >
                          {t.today.bus_number}
                        </button>
                      ) : (
                        <span style={{ color: "#9ca3af" }}>—</span>
                      )}
                    </td>
                    <td>
                      {/* 所定は乗番に付くので、便が属する乗番ごとに出す。
                          枝番で分かれた便は複数の乗番に属しうる（duties と同じ）。
                          本日充当の隣に並べて、**いつもの車と今日の車**を
                          見比べられるようにしてある。 */}
                      {(t.duties ?? []).filter((d) => d.regular).length === 0 ? (
                        <span style={{ color: "#9ca3af" }}>—</span>
                      ) : (
                        t.duties
                          .filter((d) => d.regular)
                          .map((d) => (
                            <span key={d.id} style={{ marginRight: ".5rem", display: "inline-block" }}>
                              <RegularBus regular={d.regular} />
                            </span>
                          ))
                      )}
                    </td>
                    <td style={{ whiteSpace: "normal" }}>
                      {t.top_buses.length === 0 ? (
                        <span style={{ color: "#9ca3af" }}>—</span>
                      ) : (
                        t.top_buses.map((b) => (
                          <span key={b.bus_id} style={{ marginRight: ".5rem", display: "inline-block" }}>
                            <button
                              className="anl-link"
                              onClick={() =>
                                navigate(`/analysis/buses/${encodeURIComponent(b.bus_number)}?depot=${encodeURIComponent(b.depot)}`)
                              }
                            >
                              {b.bus_number}
                            </button>
                            <span style={{ color: "#9ca3af", fontSize: ".75rem" }}>×{b.count}</span>
                            {b.depot !== data.route.depot && (
                              <span className="anl-badge anl-badge--foreign" style={{ marginLeft: ".25rem" }}>
                                {b.depot}
                              </span>
                            )}
                          </span>
                        ))
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="anl-card__body" style={{ borderTop: "1px solid var(--anl-line)" }}>
            <p className="anl-note">
              「本日充当」の薄い車号は、まだ目撃されておらず乗番から推定した車両です。
              運用変更が生じた場合は実際と異なります。<br />
              「所定」は、その便の乗番にいつも入っている車両です（直近10営業日のうち6日以上）。
              <strong>本日充当と異なっていれば代走です。</strong>
              観測が10営業日分たまっていない場合は「—」になります。<br />
              所属営業所が異なる車両が入っている便は、共管・応援・借入の可能性があります。
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
